import Download from '@assets/images/download.svg';
import { BrowserType, NotificationPermission, NotificationType } from '@constants/notification.constants';
import { LocalStorageKey } from '@constants/storage-keys.constants';
import { subscribe, unsubscribe } from '@services/push-notification.service';

export const getNotificationDescHtml = (notificationType: string, enableNotification?: () => void, resubscribe?: () => void) => {
	switch (notificationType) {
		case NotificationType.INSTALL_ANDROID:
			return <div className='font-bold text-white'>Add to Home screen to receive notifications.</div>;

		case NotificationType.INSTALL_APPLE:
			return (
				<div className='font-bold text-white'>
					<div>Get easy access and updates.</div>
					<div className='flex items-center'>
						Tap
						<div className='mx-1'>
							<Download />
						</div>
						and Add to Home Screen.
					</div>
				</div>
			);

		case NotificationType.PUSH_NOTIFICATION_ALERT:
			return (
				<div className='flex items-center flex-col desktop:items-end'>
					<div className='font-bold text-white'>
						<div className='desktop:no-underline underline underline-offset-2 inline cursor-pointer' onClick={enableNotification}>
							Turn on notifications
						</div>{' '}
						for payment alerts and important updates.
					</div>
					<div className='desktop:flex justify-end mt-4 hidden'>
						<button className='cursor-pointer w-[104px] font-semibold h-8 bg-white rounded-2xl text-blue' onClick={enableNotification}>
							Turn On
						</button>
					</div>
				</div>
			);

		case NotificationType.NOTIFICATION_OFF:
			return <div className='font-bold text-white'>Notifications are currently off. You will not receive notifications on payment alerts and important updates.{' '}</div>;

		case NotificationType.SUBSCRIPTION_ERROR:
			return (
				<div className='font-bold text-white'>
					Error enabling notification. Please{' '}
					<div className='underline underline-offset-4 inline cursor-pointer' onClick={resubscribe}>
						try again
					</div>{' '}
					or contact us if the issue persists.
				</div>
			);

		default:
			return <></>;
	}
};

export const handleNotificationPermission = async (
	uid: string,
	setNotificationOn: React.Dispatch<React.SetStateAction<boolean>>,
	handleUnsuccessfulSubscription: () => void,
	showNotificationOffAlert: () => void,
	pushNotifEventParamKey: { outlet: string, brand: string },
) => {
	try {
		const swRegistration = await navigator.serviceWorker.getRegistration();
		if (!swRegistration) {
			console.error('No service worker registration');
			return;
		}
		if (typeof Notification === 'undefined') return console.error('No Notification API');
		const permission = await Notification.requestPermission();
		switch (permission) {
			case NotificationPermission.GRANTED:
				handleGrantedPermission(uid, setNotificationOn, handleUnsuccessfulSubscription, true, pushNotifEventParamKey);
				break;

			case NotificationPermission.DENIED:
				showNotificationOffAlert();
				handleDeniedPermission(uid, setNotificationOn);
				break;

			default:
				setNotificationOn(false);
				break;
		}
	} catch (error) {
		console.error('Error handling notification permission:', error);
	}
};

export const handleDeniedPermission = async (uid: string, setNotificationOn: React.Dispatch<React.SetStateAction<boolean>>) => {
	await unsubscribe(uid);
	setNotificationOn(false);
	window.localStorage.removeItem(LocalStorageKey.IS_SUBSCRIBED);
	window.localStorage.removeItem(LocalStorageKey.REGISTRATION_ID);
};

export const handleGrantedPermission = async (
	uid: string,
	setNotificationOn: React.Dispatch<React.SetStateAction<boolean>>,
	handleUnsuccessfulSubscription: () => void,
	isFirst = false,
	eventParamKey?: { outlet: string, brand: string },
) => {
	try {
		const regId = window.localStorage.getItem(LocalStorageKey.REGISTRATION_ID);
		if (!regId) {
			const response = await subscribe(uid, false, isFirst, eventParamKey);
			if (!response?.registrationId) {
				handleUnsuccessfulSubscription();
				return;
			}
		}
		setNotificationOn(true);
	} catch (error) {
		console.error('Error handling granted permission:', error);
	}
};

export const initNotification = async (
	uid: string,
	setNotificationOn: React.Dispatch<React.SetStateAction<boolean>>,
	handleUnsuccessfulSubscription: () => void,
	setPopUpHidden: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	try {
		const swRegistration = await navigator.serviceWorker.getRegistration();
		if (!swRegistration) {
			console.error('No service worker registration');
			return;
		}
		if (typeof Notification === 'undefined') return console.error('No Notification API');
		const permission = Notification.permission;
		switch (permission) {
			case NotificationPermission.GRANTED:
				setPopUpHidden(true);
				handleGrantedPermission(uid, setNotificationOn, handleUnsuccessfulSubscription);
				break;

			case NotificationPermission.DENIED:
				setPopUpHidden(false);
				await handleDeniedPermission(uid, setNotificationOn);
				break;
		}
	} catch (error) {
		console.error('Error initialiazing notification: ', error);
	}
};

export const getEnableNotificationGuideText = (browser?: BrowserType | null) => {
	const steps = {
		[BrowserType.EDGE]: [
			'Click on the three dots (menu) in the top-right corner of Edge.',
			'Select “Settings”.',
			'Click on “Cookies and site permissions” in the left sidebar.',
			'Scroll down and click on “Notifications”.',
			'Find your site under “Block”.',
			'Click on the three dots next to your site and select “Allow”.',
		],
		[BrowserType.CHROME]: [
			'Click on the three dots (menu) in the top-right corner of Chrome.',
			'Select “Settings”.',
			'Scroll down and click on “Privacy and security”.',
			'Click on “Site Settings”.',
			'Under “Permissions”, click on “Notifications”.',
			'Find your site in the “Not allowed to send notifications” list.',
			'Click on the three dots next to your site and select “Allow”.',
		],
		[BrowserType.FIREFOX]: [
			'Click on the three horizontal lines (menu) in the top-right corner of Firefox.',
			'Select “Settings”.',
			'Click on “Privacy & Security” in the left sidebar.',
			'Scroll down to the “Permissions” section.',
			'Next to “Notifications”, click on the “Settings…” button.',
			'Find your site in the list and change the status to “Allow”.',
		],
		[BrowserType.SAFARI]: [
			'Click on “Safari” in the top menu bar.',
			'Select “Preferences”.',
			'Go to the “Websites” tab.',
			'Select “Notifications” in the left sidebar.',
			'Find your site in the list and change the status to “Allow”.',
		],
	};

	if (browser && steps[browser]) {
		return (
			<ol className='list-outside pl-5'>
				{steps[browser].map((step, idx) => (
					<li key={idx}>{step}</li>
				))}
			</ol>
		);
	}

	return (
		<ol className='list-outside pl-5'>
			<li>Open your device's Settings</li>
			<li>Tap Notifications / App Notifications</li>
			<li>Tap Kris+ Cashier</li>
			<li>Turn ON the app's notifications</li>
		</ol>
	);
};

export const checkStandaloneMode = () => {
	return window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && navigator.standalone === true);
};